/**
 * Borrower application (docs/M2-CASE-API.md §4, PLAN PR 8). Step payloads are stored as
 * jsonb keyed by step id and validated with zod in the API; these are the wire shapes both
 * the wizard and the API agree on. Nothing here is a prohibited-basis field and nothing here
 * is an SSN, EIN or date of birth: partner-specific identifiers travel through the
 * partner-directed document channel, never through the generic form.
 */
import type { BusinessEntityType, BuyerAddressView, CaseStage, MoneyView } from './api-types'

export const APPLICATION_STEPS = ['business', 'owners', 'financials', 'equipment_review'] as const
export type ApplicationStep = (typeof APPLICATION_STEPS)[number]

/** Bump when a step payload shape changes; frozen into every revision. */
export const APPLICATION_SCHEMA_VERSION = 'application-2026-09.1'

export const FINANCIAL_FACT_SOURCES = ['stated', 'tax_return', 'financial_statement', 'bank_statements', 'accounting_software'] as const
export type FinancialFactSource = (typeof FINANCIAL_FACT_SOURCES)[number]

export const FINANCIAL_PERIODS = ['trailing_12_months', 'fiscal_year', 'calendar_year', 'monthly'] as const
export type FinancialPeriod = (typeof FINANCIAL_PERIODS)[number]

/**
 * A number the borrower states about the business. `unknown` is a legitimate answer that a
 * program requirement can later refuse to accept; it is never coerced to zero.
 */
export type FinancialFact =
  | { status: 'stated'; amount: MoneyView; period: FinancialPeriod; period_end: string | null; source: FinancialFactSource }
  | { status: 'unknown' }

export interface BusinessStepPayload {
  legal_name: string
  trading_name: string | null
  entity_type: BusinessEntityType
  /** ISO date the entity was formed. */
  formed_on: string
  /** ISO date operations began, when different from formation. */
  operating_since: string | null
  industry: string
  business_address: BuyerAddressView
  /** Where the equipment will live; null means the business address. */
  equipment_use_address: BuyerAddressView | null
  representative: { name: string; title: string; email: string; phone: string }
}

export interface OwnerEntry {
  full_name: string
  title: string
  /** Whole-percent ownership; the list must not exceed 100. */
  ownership_percent: number
  email: string
  phone: string | null
  address: BuyerAddressView | null
}

export interface OwnersStepPayload {
  owners: OwnerEntry[]
}

export interface FinancialsStepPayload {
  /** The borrower's own view of what they are asking for; compared against the dealer's quote. */
  requested_amount: MoneyView
  intended_use: string
  repayment_source: string
  annual_revenue: FinancialFact
  monthly_debt_service: FinancialFact
}

export interface EquipmentItemFlag {
  equipment_item_id: string
  note: string
}

export interface EquipmentReviewStepPayload {
  /** The borrower confirms the dealer's purchase details match what they agreed. */
  quote_matches: boolean
  note: string | null
  item_flags: EquipmentItemFlag[]
}

export interface ApplicationStepPayloads {
  business: BusinessStepPayload
  owners: OwnersStepPayload
  financials: FinancialsStepPayload
  equipment_review: EquipmentReviewStepPayload
}

/** What the borrower has saved so far; a step is absent until first saved. */
export type ApplicationDraftSteps = Partial<ApplicationStepPayloads>

export const DISCREPANCY_KINDS = [
  'requested_amount',
  'business_name',
  'entity_type',
  'business_state',
  'equipment_use_state',
  'quote_disputed',
] as const
export type DiscrepancyKind = (typeof DISCREPANCY_KINDS)[number]

/**
 * A borrower answer that disagrees with what the dealer entered. Both values are kept; the
 * reviewer reconciles (AC06). Money values are `MoneyView`, others are strings.
 */
export interface DiscrepancyView {
  kind: DiscrepancyKind
  dealer_value: MoneyView | string | null
  borrower_value: MoneyView | string | null
  note: string | null
}

export const CONSENT_PURPOSES = ['application_processing', 'electronic_communications', 'share_with_partner'] as const
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number]

/** Purposes that must be granted for a submission to be accepted. */
export const REQUIRED_CONSENT_PURPOSES: readonly ConsentPurpose[] = ['application_processing', 'electronic_communications']

export interface Disclosure {
  purpose: ConsentPurpose
  version: string
  title: string
  text: string
}

/**
 * Placeholder disclosure texts for the demo. Counsel-approved wording replaces these before
 * anything live (docs/OWNER-CHECKLIST.md); the server hashes the text it has, so a stale
 * client version is rejected rather than recorded.
 */
export const DISCLOSURES: Record<ConsentPurpose, Disclosure> = {
  application_processing: {
    purpose: 'application_processing',
    version: 'demo-2026-09.1',
    title: 'Processing your application',
    text: 'I authorize Tex Finance to receive and review the information in this application for the purpose of arranging commercial equipment financing for the business named here. I confirm I am authorized to act for that business and that the information is accurate to the best of my knowledge. DEMO TEXT — not a legal disclosure.',
  },
  electronic_communications: {
    purpose: 'electronic_communications',
    version: 'demo-2026-09.1',
    title: 'Electronic records and communications',
    text: 'I agree to receive records, notices and communications about this request electronically, at the email address I signed in with. DEMO TEXT — not a legal disclosure.',
  },
  share_with_partner: {
    purpose: 'share_with_partner',
    version: 'demo-2026-09.1',
    title: 'Sharing with financing partners',
    text: 'I authorize Tex Finance to share this application and supporting documents with financing partners it selects, for the purpose of obtaining financing offers. Tex will tell me which partner received it. DEMO TEXT — not a legal disclosure.',
  },
}

/** POST /cases/:id/application/submit body item. */
export interface ConsentInput {
  purpose: ConsentPurpose
  disclosure_version: string
  granted: true
}

export interface ConsentRecordView {
  purpose: ConsentPurpose
  disclosure_version: string
  granted: boolean
  recorded_at: string
}

/** GET /cases/:id/application — borrower and Tex; a dealer is refused. */
export interface ApplicationView {
  case_id: string
  stage: CaseStage
  /** Draft optimistic-concurrency token; `0` before the first save. Send back as `expected_version`. */
  version: number
  completed_steps: ApplicationStep[]
  steps: ApplicationDraftSteps
  /** Live comparison of the draft with the dealer's request. */
  discrepancies: DiscrepancyView[]
  /** False once submitted or when the case has left the application stages. */
  editable: boolean
  submitted: {
    revision_number: number
    submitted_at: string
    consents: ConsentRecordView[]
  } | null
  updated_at: string | null
}

export interface ApplicationStepPutBody<S extends ApplicationStep = ApplicationStep> {
  expected_version: number
  payload: ApplicationStepPayloads[S]
}

export interface ApplicationSubmitBody {
  expected_version: number
  consents: ConsentInput[]
}
